import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  MapPinIcon,
  CurrencyDollarIcon,
  TruckIcon,
  BuildingStorefrontIcon,
  GiftIcon,
  CubeIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import Header from './Header';
import Footer from './Footer';
import FooterCopyright from './FooterCopyright';
import LeadForm from './LeadForm';
import PlanCards from './PlanCards';
import { useLeadSignup } from './LeadSignupContext';
import {
  PLANS,
  SHARED,
  CITIES,
  NICHES,
  HOW_IT_WORKS,
  WHY_CHEAPER,
  FAQS,
  CONTACT_EMAIL,
} from '../utils/product';

const NICHE_ICONS = [
  ShoppingBagIcon,
  CubeIcon,
  GiftIcon,
  Squares2X2Icon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
];

const FretzHomePage = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const { openSignup } = useLeadSignup();

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-dots bg-dots-md opacity-60" aria-hidden="true" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-brand-50/60 rounded-l-[80px] hidden lg:block" aria-hidden="true" />

        <div className="relative container mx-auto px-4 lg:px-6 pt-14 pb-8 lg:pt-20 lg:pb-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="pt-2">
              <span className="badge mb-6">
                <MapPinIcon className="h-3.5 w-3.5" />
                Rotas B2B · Grande Goiânia
              </span>

              <h1 className="font-display text-display-xl text-ink-900">
                Planos de rota mensal a partir de{' '}
                <span className="relative inline-block">
                  <span className="relative z-10 bg-clip-text text-transparent bg-brand-gradient">
                    {SHARED.fromPrice}
                  </span>
                  <span className="absolute bottom-1 left-0 right-0 h-3 bg-accent-200/60 -z-0 rounded" aria-hidden="true" />
                </span>
              </h1>

              <p className="mt-5 text-xl text-ink-500 max-w-lg leading-relaxed">
                Frota identificada Fretz em circuitos compartilhados. Coleta diária,
                semanal ou mensal. Sua empresa troca carro + motorista + rota ruim
                por um plano previsível. Pacotes leves, Grande Goiânia.
              </p>

              <div className="mt-8 grid grid-cols-3 gap-4">
                {[
                  { value: SHARED.fromPrice, label: 'A partir de / mês' },
                  { value: '3 planos', label: 'Diário, semanal, mensal' },
                  { value: SHARED.doorWait, label: 'Espera máxima na porta' },
                ].map(({ value, label }) => (
                  <div key={label} className="p-4 rounded-2xl bg-white border border-ink-100 shadow-soft">
                    <p className="font-display text-xl font-extrabold text-brand-700">{value}</p>
                    <p className="text-xs text-ink-500 mt-1">{label}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-ink-500">
                {[
                  [ClockIcon, 'SLA 36h ou 72h úteis'],
                  [TruckIcon, 'Frota compartilhada Fretz'],
                  [MapPinIcon, SHARED.coverage],
                ].map(([Icon, label]) => (
                  <div key={label} className="flex items-center gap-1.5">
                    <Icon className="h-4 w-4 text-brand-600" />
                    <span>{label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a href="#plano" className="btn-accent">
                  Ver os 3 planos
                </a>
                <Link to="/como-funciona" className="btn-secondary gap-2">
                  Como funciona <ChevronRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </div>

            <div className="card p-6 lg:p-8 relative z-10">
              <p className="eyebrow mb-2">Fretz Empresas</p>
              <h2 className="font-display text-2xl font-bold text-ink-900">Escolha o ritmo da coleta</h2>
              <p className="mt-2 text-sm text-ink-500 mb-4">
                Pacote até {SHARED.maxSize} e {SHARED.maxWeight}. {SHARED.doorRule}
              </p>
              <div className="divide-y divide-ink-100">
                {PLANS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openSignup(p.id)}
                    className="w-full flex items-start justify-between gap-4 py-4 group text-left"
                  >
                    <div>
                      <p className="font-semibold text-ink-900 group-hover:text-brand-700">{p.name}</p>
                      <p className="text-xs text-ink-500 mt-1">{p.cadence}</p>
                    </div>
                    <p className="font-display text-xl font-extrabold text-brand-700 shrink-0">
                      {p.priceLabel}
                    </p>
                  </button>
                ))}
              </div>
              <a href="#contato" className="mt-6 btn-primary w-full py-4 text-base">
                Quero Fretz Empresas
              </a>
              <p className="mt-3 text-center text-xs text-ink-400">
                Ou escreva para{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-700 underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="relative overflow-hidden bg-ink-900 py-4 border-y border-white/5">
        <div className="marquee-track">
          {[...CITIES, ...CITIES].map((c, i) => (
            <span key={`${c}-${i}`} className="flex items-center gap-3 text-white/70 text-sm font-semibold whitespace-nowrap">
              <span className="h-1.5 w-1.5 rounded-full bg-accent-400" />
              {c}
            </span>
          ))}
        </div>
      </div>

      <section className="section bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="eyebrow mb-2">Planos</p>
            <h2 className="font-display text-display-lg text-ink-900">Diário, semanal ou mensal</h2>
            <p className="mt-3 text-ink-500">
              Mesmas regras de pacote e porta. O que muda é o ritmo da coleta e o quanto de paradas está incluso.
            </p>
          </div>
          <PlanCards />
        </div>
      </section>

      <section id="feitos-para" className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="eyebrow mb-2">Feito para</p>
            <h2 className="font-display text-display-lg text-ink-900">
              Quem já gasta caro com frete próprio
            </h2>
            <p className="mt-3 text-ink-500">
              Exemplos concretos de quem encaixa: volume de sacola e pacote,
              vários destinos, sem precisar de caminhão.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {NICHES.map((niche, i) => {
              const Icon = NICHE_ICONS[i];
              return (
                <div key={niche.title} className="card p-6 hover:-translate-y-1 transition-transform duration-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
                      <Icon className="h-6 w-6" />
                    </div>
                    <span className="badge-brand">{niche.tag}</span>
                  </div>
                  <h3 className="font-display text-lg font-bold text-ink-900 mt-4">{niche.title}</h3>
                  <p className="text-ink-500 mt-2 text-sm leading-relaxed">{niche.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="como-funciona" className="section bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12">
            <p className="eyebrow mb-2">Processo</p>
            <h2 className="font-display text-display-lg text-ink-900">Como funciona a rota</h2>
          </div>
          <div className="grid md:grid-cols-5 gap-px bg-ink-100 rounded-3xl overflow-hidden">
            {HOW_IT_WORKS.map(({ n, title, desc }) => (
              <div key={n} className="bg-white p-6 lg:p-8">
                <span className="font-display text-4xl font-extrabold text-brand-100">{n}</span>
                <h3 className="font-display text-lg font-bold text-ink-900 mt-4">{title}</h3>
                <p className="text-ink-500 mt-2 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/como-funciona" className="btn-ghost gap-1">
              Ver detalhes <ChevronRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="eyebrow mb-2">Por que é mais barato</p>
            <h2 className="font-display text-display-lg text-ink-900">
              A rota compartilhada corta o custo ocioso
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {WHY_CHEAPER.map(({ title, desc }, i) => {
              const Icon = [TruckIcon, ClockIcon, CurrencyDollarIcon][i];
              return (
                <div key={title} className="card p-6 lg:p-8">
                  <div className="h-12 w-12 rounded-2xl bg-brand-gradient text-white flex items-center justify-center shadow-glow">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-lg font-bold text-ink-900 mt-4">{title}</h3>
                  <p className="text-ink-500 mt-2 text-sm leading-relaxed">{desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section bg-ink-50">
        <div className="container mx-auto px-4 lg:px-6 max-w-3xl">
          <div className="text-center mb-10">
            <p className="eyebrow mb-2">Dúvidas</p>
            <h2 className="font-display text-display-lg text-ink-900">FAQ</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((item, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div key={item.q} className="card overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : idx)}
                    className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-ink-50 transition"
                    aria-expanded={isOpen}
                  >
                    <span className="font-semibold text-ink-900">{item.q}</span>
                    <ChevronDownIcon className={`h-5 w-5 text-ink-500 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 text-ink-600 text-sm leading-relaxed fade-up">{item.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <p className="eyebrow mb-2">Comece este mês</p>
              <h2 className="font-display text-display-lg text-ink-900">
                Troque o carro da empresa pela rota Fretz
              </h2>
              <p className="mt-4 text-ink-500 text-lg leading-relaxed">
                Três planos, a partir de {SHARED.fromPrice}/mês. Pacotes de até {SHARED.maxSize} e {SHARED.maxWeight}.
                Sem simulador de picape, sem mudança, sem marketplace de caminhão.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Frota identificada em rota compartilhada',
                  `Encontre o veículo em até ${SHARED.doorWait}`,
                  `Cobertura: ${SHARED.coverage}`,
                  'Diário · Semanal · Mensal',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-ink-700">
                    <CheckIcon className="h-5 w-5 text-brand-600 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            <div className="card p-6 lg:p-8">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FooterCopyright />
    </div>
  );
};

export default FretzHomePage;
